import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { FileText, Calendar, Tag, Download, Edit2, Save, X, Clock, Folder, Plus, Trash2, MessageSquare } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Document as DocumentType, Folder as FolderType } from '../types';
import { formatDate } from '../utils/dateUtils';

const DocumentDetail: React.FC = () => {
  const { docId } = useParams<{ docId: string }>();
  const [docData, setDocData] = React.useState<DocumentType | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState(false);
  const [isEditingSummary, setIsEditingSummary] = React.useState(false);
  const [editedSummary, setEditedSummary] = React.useState('');
  const [savingSummary, setSavingSummary] = React.useState(false);
  const [showFolderSelector, setShowFolderSelector] = React.useState(false);
  const [availableFolders, setAvailableFolders] = React.useState<FolderType[]>([]);
  const [selectedFolderIds, setSelectedFolderIds] = React.useState<string[]>([]);
  const [associating, setAssociating] = React.useState(false);

  React.useEffect(() => {
    const fetchDocument = async () => {
      if (!docId) return;
      
      try {
        setLoading(true);
        const { apiService } = await import('../services/api');
        const doc = await apiService.getDocument(docId);
        setDocData(doc);
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const isNotFound = status === 404 || (err instanceof Error && err.message?.includes('404'));
        setError(
          isNotFound
            ? 'DOCUMENT_UNAVAILABLE'
            : err instanceof Error
              ? err.message
              : 'Failed to load document'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [docId]);

  // Fetch available folders when folder selector is shown
  React.useEffect(() => {
    const fetchFolders = async () => {
      if (!showFolderSelector) return;
      
      try {
        const { apiService } = await import('../services/api');
        const folders = await apiService.getFolders(true, 'user');
        setAvailableFolders(folders);
        
        // Pre-select currently associated folders
        const currentFolderIds = docData?.folder_ids || [];
        setSelectedFolderIds([...currentFolderIds]);
      } catch (err) {
        toast.error('Failed to load folders');
        console.error(err);
      }
    };

    fetchFolders();
  }, [showFolderSelector, docData?.folder_ids]);

  const handleDownload = async () => {
    if (!docId) return;
    try {
      setDownloading(true);
      const { apiService } = await import('../services/api');
      const blob = await apiService.downloadDocument(docId);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = docData?.filename || `${docId}.bin`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to download document';
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleEditSummary = () => {
    if (docData) {
      setEditedSummary(docData.summary_text || '');
      setIsEditingSummary(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingSummary(false);
    setEditedSummary('');
  };

  const handleSaveSummary = async () => {
    if (!docId) return;
    try {
      setSavingSummary(true);
      const { apiService } = await import('../services/api');
      const updatedDoc = await apiService.updateDocumentSummary(docId, editedSummary);
      setDocData(updatedDoc);
      setIsEditingSummary(false);
      toast.success('Summary updated successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update summary';
      toast.error(message);
    } finally {
      setSavingSummary(false);
    }
  };

  const handleAssociateFolders = async () => {
    if (!docId) return;
    try {
      setAssociating(true);
      const { apiService } = await import('../services/api');
      
      // Get currently associated folders
      const currentFolderIds = docData?.folder_ids || [];
      
      // Find folders to add (selected but not currently associated)
      const foldersToAdd = selectedFolderIds.filter(id => !currentFolderIds.includes(id));
      
      console.log('Current folder IDs:', currentFolderIds);
      console.log('Selected folder IDs:', selectedFolderIds);
      console.log('Folders to add:', foldersToAdd);
      
      if (foldersToAdd.length === 0) {
        toast('No new folders to associate', { icon: 'ℹ️' });
        setShowFolderSelector(false);
        return;
      }
      
      // Associate new folders
      const updatedDoc = await apiService.associateDocumentWithFolders(docId, foldersToAdd);
      console.log('Updated document from API:', updatedDoc);
      console.log('Updated folder_ids:', updatedDoc.folder_ids);
      console.log('Updated folder_names:', updatedDoc.folder_names);
      setDocData(updatedDoc);
      setShowFolderSelector(false);
      toast.success(`Associated document with ${foldersToAdd.length} folder(s)`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to associate folders';
      toast.error(message);
    } finally {
      setAssociating(false);
    }
  };

  const handleDisassociateFolder = async (folderId: string, folderName: string) => {
    if (!docId) return;
    
    // Prevent removing last folder if document is not global
    const currentFolderIds = docData?.folder_ids || [];
    if (currentFolderIds.length <= 1 && !docData?.is_global) {
      toast.error('Cannot remove the last folder association from a non-global document');
      return;
    }
    
    if (!window.confirm(`Remove document from folder "${folderName}"?`)) {
      return;
    }
    
    try {
      const { apiService } = await import('../services/api');
      const updatedDoc = await apiService.disassociateDocumentFromFolder(docId, folderId);
      setDocData(updatedDoc);
      toast.success(`Removed from folder "${folderName}"`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to disassociate folder';
      toast.error(message);
    }
  };

  const toggleFolderSelection = (folderId: string) => {
    setSelectedFolderIds(prev => {
      if (prev.includes(folderId)) {
        return prev.filter(id => id !== folderId);
      } else {
        return [...prev, folderId];
      }
    });
  };


  const formatProcessingTime = (seconds: number): string => {
    if (!seconds || seconds === 0) return 'N/A';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    if (minutes > 0 && remainingSeconds > 0) {
      return `${minutes} min ${remainingSeconds} sec`;
    } else if (minutes > 0) {
      return `${minutes} min`;
    } else {
      return `${remainingSeconds} sec`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !docData) {
    const isUnavailable = error === 'DOCUMENT_UNAVAILABLE';
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] w-full px-6">
        <div className="text-center max-w-md mx-auto">
          {isUnavailable ? (
            <>
              <p className="text-gray-700 text-lg font-medium mb-2">
                As of now we're unable to access this document.
              </p>
              <p className="text-gray-600">
                If you face this issue persistently, please{' '}
                <Link to="/contact" className="text-primary-600 hover:underline font-medium">
                  Contact Us
                </Link>
                .
              </p>
            </>
          ) : (
            <p className="text-red-600 mb-4">{error || 'Document not found'}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">{docData.title}</h1>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading ? 'Preparing...' : 'Download'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Document Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-8 w-8 text-primary-600" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">{docData.filename}</h2>
                <p className="text-gray-600">{docData.title}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(docData.created_at, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Tag className="h-4 w-4" />
                <span className={`px-2 py-1 rounded-full ${
                  docData.status === 'ready' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {docData.status}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <FileText className="h-4 w-4" />
                <span>{docData.chunks} chunks</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span className="text-gray-500">Size:</span>
                <span>{(docData.file_size / 1024).toFixed(2)} KB</span>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Summary</h3>
              {!isEditingSummary && (docData.is_global || docData.summary_text) && (
                <button
                  onClick={handleEditSummary}
                  className="inline-flex items-center px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                  title="Edit summary"
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </button>
              )}
            </div>
            {isEditingSummary ? (
              <div className="space-y-3">
                <textarea
                  value={editedSummary}
                  onChange={(e) => setEditedSummary(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[150px]"
                  placeholder="Enter document summary..."
                />
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSaveSummary}
                    disabled={savingSummary}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {savingSummary ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={savingSummary}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-700 whitespace-pre-wrap">
                {docData.summary_text || (docData.is_global ? 'No summary available. Click Edit to add one.' : 'No summary available.')}
              </p>
            )}
          </div>

          {/* Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Details</h3>
            <dl className="grid grid-cols-1 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Document ID</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{docData.doc_id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Content Type</dt>
                <dd className="mt-1 text-sm text-gray-900">{docData.content_type}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Embedding Model</dt>
                <dd className="mt-1 text-sm text-gray-900">{docData.embedding_model}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Ingestion Method</dt>
                <dd className="mt-1 text-sm text-gray-900">{docData.ingestion_method}</dd>
              </div>
              {docData.thread_id && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Thread
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {docData.thread_name || docData.thread_id}
                    {docData.thread_id && (
                      <span className="text-gray-500 font-mono text-xs ml-2">({docData.thread_id})</span>
                    )}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center justify-between">
                  <span className="flex items-center">
                    <Folder className="h-4 w-4 mr-1" />
                    Associated Folders
                  </span>
                  {!showFolderSelector && (
                    <button
                      onClick={() => setShowFolderSelector(true)}
                      className="inline-flex items-center px-2 py-1 text-xs text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                      title="Associate with more folders"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </button>
                  )}
                </dt>
                <dd className="mt-1">
                  {showFolderSelector ? (
                    <div className="space-y-3">
                      <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                        {availableFolders.length === 0 ? (
                          <p className="text-sm text-gray-500 py-2">No folders available</p>
                        ) : (
                          availableFolders.map((folder) => {
                            const isSelected = selectedFolderIds.includes(folder.id);
                            const isCurrentlyAssociated = docData?.folder_ids?.includes(folder.id);
                            return (
                              <label
                                key={folder.id}
                                className={`flex items-center px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${
                                  isSelected ? 'bg-primary-50' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleFolderSelection(folder.id)}
                                  disabled={isCurrentlyAssociated}
                                  className="mr-2 h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                                />
                                <span className={`text-sm flex-1 ${isCurrentlyAssociated ? 'text-gray-400' : 'text-gray-900'}`}>
                                  {folder.name}
                                  {isCurrentlyAssociated && <span className="text-xs ml-2">(already associated)</span>}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleAssociateFolders}
                          disabled={associating || selectedFolderIds.length === 0}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {associating ? 'Associating...' : 'Associate Selected'}
                        </button>
                        <button
                          onClick={() => {
                            setShowFolderSelector(false);
                            setSelectedFolderIds(docData?.folder_ids || []);
                          }}
                          disabled={associating}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(docData.folder_names && docData.folder_names.length > 0) ? (
                        docData.folder_names.map((folderName, index) => {
                          const folderId = docData.folder_ids?.[index];
                          return (
                            <div
                              key={folderId || index}
                              className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md border border-gray-200"
                            >
                              <span className="text-sm text-gray-900 flex items-center">
                                <Folder className="h-3 w-3 mr-2 text-gray-500" />
                                {folderName}
                                {index === 0 && (
                                  <span className="ml-2 text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                                    Primary
                                  </span>
                                )}
                              </span>
                              {folderId && (
                                <button
                                  onClick={() => handleDisassociateFolder(folderId, folderName)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                                  title={`Remove from folder "${folderName}"`}
                                  disabled={docData.folder_ids?.length === 1 && !docData.is_global}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          );
                        })
                      ) : docData.folder_name ? (
                        <span className="text-sm text-gray-900">{docData.folder_name}</span>
                      ) : (
                        <span className="text-sm text-gray-500">No folders associated</span>
                      )}
                    </div>
                  )}
                </dd>
              </div>
              {docData.processing_status && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Processing Status</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">{docData.processing_status}</dd>
                </div>
              )}
              {docData.processing_times && docData.processing_times.total !== undefined && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    Total Processing Time
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    {formatProcessingTime(docData.processing_times.total)}
                  </dd>
                </div>
              )}
              {docData.processing_times && Object.keys(docData.processing_times).length > 0 && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-2">Processing Times by Phase</dt>
                  <dd className="mt-1 space-y-1">
                    {Object.entries(docData.processing_times)
                      .filter(([key]) => key !== 'total')
                      .map(([phase, time]) => (
                        <div key={phase} className="flex justify-between items-center text-sm">
                          <span className="text-gray-600 capitalize">{phase.replace('_', ' ')}:</span>
                          <span className="text-gray-900 font-medium">{formatProcessingTime(time as number)}</span>
                        </div>
                      ))}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetail;

